import type { Classroom, DailyMemo } from "./types.js";

export type DailyMemoAction =
  | { type: "create"; id: string; title: string }
  | { type: "delete"; id: string }
  | { type: "setStudent"; id: string; studentId: string; selected: boolean };

const defaults: DailyMemo[] = [
  { id: "phone", title: "平日不帶電話", studentIds: [] },
  { id: "meal", title: "不訂飯", studentIds: [] },
  { id: "byod", title: "不參與 BYOD", studentIds: [] },
];

export function dailyMemos(classroom: Pick<Classroom, "dailyMemos">): DailyMemo[] {
  // An explicit empty list records the teacher's choice to remove all defaults.
  return (classroom.dailyMemos ?? defaults).map((memo) => ({ ...memo, studentIds: [...memo.studentIds] }));
}
export function applyDailyMemoAction(classroom: Pick<Classroom, "dailyMemos">, action: DailyMemoAction): DailyMemo[] {
  if (classroom.dailyMemos !== undefined && !isValidDailyMemos(classroom.dailyMemos)) throw new Error("invalid-memos");
  const memos = dailyMemos(classroom);
  if (!action.id?.trim()) throw new Error("invalid-memo");
  const existing = memos.find((memo) => memo.id === action.id);
  if (action.type === "create") {
    const title = action.title.trim();
    if (!title || title.length > 60) throw new Error("invalid-memo-title");
    if (existing || memos.some((memo) => memo.title.toLowerCase() === title.toLowerCase()))
      throw new Error("duplicate-memo");
    return [...memos, { id: action.id, title, studentIds: [] }];
  }
  if (!existing) throw new Error("stale-memo");
  if (action.type === "delete") return memos.filter((memo) => memo.id !== action.id);
  if (!action.studentId?.trim() || typeof action.selected !== "boolean") throw new Error("invalid-memo-student");
  return memos.map((memo) =>
    memo.id !== action.id
      ? memo
      : {
          ...memo,
          studentIds: action.selected
            ? [...new Set([...memo.studentIds, action.studentId])]
            : memo.studentIds.filter((id) => id !== action.studentId),
        },
  );
}
export function isValidDailyMemos(value: unknown): value is DailyMemo[] {
  if (!Array.isArray(value)) return false;
  const ids = new Set<string>();
  const titles = new Set<string>();
  return value.every((memo) => {
    if (
      !memo ||
      typeof memo !== "object" ||
      typeof memo.id !== "string" ||
      !memo.id.trim() ||
      typeof memo.title !== "string" ||
      !memo.title.trim() ||
      memo.title.length > 60 ||
      !Array.isArray(memo.studentIds) ||
      !memo.studentIds.every((id: unknown) => typeof id === "string" && !!id.trim()) ||
      new Set(memo.studentIds).size !== memo.studentIds.length
    )
      return false;
    const title = memo.title.trim().toLowerCase();
    if (ids.has(memo.id) || titles.has(title)) return false;
    ids.add(memo.id);
    titles.add(title);
    return true;
  });
}
